<template>
  <div>
    <v-data-table :headers="headers" :items="rows" :search="search" item-key="id" :sort-by="'deletedAt'" :sort-desc="true" :no-data-text="$t('components.DeletedModels.empty')">
      <template v-slot:top>
        <v-toolbar flat color="white">
          <span class="text-body-2 grey--text text--darken-1">{{ $t('components.DeletedModels.description') }}</span>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('components.DeletedModels.search')"
            single-line
            hide-details
          ></v-text-field>
        </v-toolbar>
      </template>
      <template v-slot:item.deletedAt="{ item }">
        {{ item.deletedDate }}
      </template>
      <template v-slot:item.daysLeft="{ item }">
        <span :class="item.daysLeft <= 2 ? 'error--text' : ''">{{ $t('components.DeletedModels.daysLeft', { count: item.daysLeft }) }}</span>
      </template>
      <template v-slot:item.action="{ item }">
        <v-btn small text color="primary" :loading="restoringId === item.id" :disabled="restoringId !== null" @click="openRestoreDialog(item)">
          <v-icon left small>mdi-restore</v-icon>
          {{ $t('components.DeletedModels.restore') }}
        </v-btn>
      </template>
    </v-data-table>
    <v-dialog v-if="restoreDialog" v-model="restoreDialog" max-width="450" persistent>
      <v-card>
        <v-card-title class="headline">{{ $t('components.DeletedModels.restoreTitle') }}</v-card-title>
        <v-card-text>
          <div>{{ $t(restoreItem.isEkrm ? 'components.DeletedModels.restoreMessageEkrm' : 'components.DeletedModels.restoreMessage', { name: restoreItem.name }) }}</div>
          <template v-if="nameConflict">
            <div class="mt-4 error--text">{{ $t('components.DeletedModels.nameConflict') }}</div>
            <v-text-field v-model="newName" :label="$t('components.DeletedModels.newNameLabel')" autofocus></v-text-field>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="gray darken-1" text @click="closeRestoreDialog">{{ $t('components.DeletedModels.cancel') }}</v-btn>
          <v-btn color="primary" text :disabled="nameConflict && !newName.trim()" @click="restore">{{ $t('components.DeletedModels.restore') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snack" :timeout="8000" :color="snackColor">
      {{ snackText }}
      <v-btn text @click="snack = false">{{ $t('components.DeletedModels.close') }}</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

const DAY = 24 * 60 * 60 * 1000
// What the restore callable can refuse with, and what it can warn about once done.
const ERRORS = ['target-exists', 'storage-changed', 'trash-entry-not-found', 'permission-denied']
const WARNINGS = ['version-unlinked', 'request-exists', 'ekrm-missing']
// The type of an entry that holds an EKRM deleted on its own, from Manage EKRM, rather than a model.
const STANDALONE_EKRM = 'standalone_ekrm'

export default {
  name: 'DeletedModels',
  data () {
    return {
      search: '',
      restoreDialog: false,
      restoreItem: null,
      restoringId: null,
      nameConflict: false,
      newName: '',
      snack: false,
      snackColor: '',
      snackText: ''
    }
  },
  computed: {
    ...mapGetters('manager', [
      'deletedModels'
    ]),
    headers () {
      return [
        { text: this.$t('components.DeletedModels.headerName'), value: 'name' },
        { text: this.$t('components.DeletedModels.headerType'), value: 'typeLabel' },
        { text: this.$t('components.DeletedModels.headerDeletedBy'), value: 'deletedBy' },
        { text: this.$t('components.DeletedModels.headerDeleted'), value: 'deletedAt' },
        { text: this.$t('components.DeletedModels.headerDaysLeft'), value: 'daysLeft' },
        { text: this.$t('components.DeletedModels.headerSource'), value: 'sourceLabel' },
        { text: '', value: 'action', sortable: false, align: 'end' }
      ]
    },
    rows () {
      const now = Date.now()
      // An entry being purged can no longer be restored.
      return this.deletedModels.filter(entry => entry.purging !== true).map(entry => {
        // date_deleted is null for an instant on the entry's first snapshot, until the server sets it.
        const deletedAt = entry.date_deleted ? entry.date_deleted.toMillis() : now
        const purgeAt = entry.purge_after ? entry.purge_after.toMillis() : now
        return {
          id: entry.id,
          name: entry.name || entry.client_id || entry.ekrm_id,
          isEkrm: entry.type === STANDALONE_EKRM,
          typeLabel: this.typeLabel(entry.type),
          deletedBy: entry.deleted_by_name || '',
          deletedAt: deletedAt,
          deletedDate: new Date(deletedAt).toLocaleDateString(),
          daysLeft: Math.max(0, Math.ceil((purgeAt - now) / DAY)),
          sourceLabel: entry.source ? this.$t('components.DeletedModels.source.' + entry.source) : ''
        }
      })
    }
  },
  methods: {
    ...mapActions('manager', [
      'loadDeletedModels',
      'stopDeletedModels',
      'restoreDeletedModel'
    ]),
    typeLabel (type) {
      if (type === 'mmm') return this.$t('components.DeletedModels.typeAdHoc')
      if (type === 'automatic') return this.$t('components.DeletedModels.typeDiy')
      if (type === 'ekrm') return this.$t('components.DeletedModels.typeCustomEkrm')
      if (type === STANDALONE_EKRM) return this.$t('components.DeletedModels.typeEkrm')
      return this.$t('components.DeletedModels.typeCustomReference')
    },
    openRestoreDialog (item) {
      this.restoreItem = item
      this.nameConflict = false
      this.newName = ''
      this.restoreDialog = true
    },
    closeRestoreDialog () {
      this.restoreDialog = false
      this.restoreItem = null
    },
    showSnack (color, text) {
      this.snackColor = color
      this.snackText = text
      this.snack = true
    },
    restore () {
      const item = this.restoreItem
      const newName = this.nameConflict ? this.newName.trim() : ''
      this.restoreDialog = false
      this.restoringId = item.id
      this.restoreDeletedModel({ trashId: item.id, newName: newName }).then(result => {
        const notes = result.warnings.filter(warning => WARNINGS.includes(warning))
          .map(warning => this.$t('components.DeletedModels.warning.' + warning))
        if (result.reportPending) {
          notes.push(this.$t('components.DeletedModels.reportPending'))
        }
        const restored = this.$t('components.DeletedModels.restored', { name: result.name })
        this.showSnack(notes.length > 0 ? 'warning' : 'success', [restored].concat(notes).join(' '))
        this.restoreItem = null
      }).catch(error => {
        if (error.message === 'name-conflict') {
          // Ask for another name and let the user try again.
          this.nameConflict = true
          this.newName = this.$t('components.DeletedModels.restoredName', { name: newName || item.name })
          this.restoreDialog = true
          return
        }
        console.error(error)
        const reason = ERRORS.includes(error.message) ? error.message : (String(error.code).indexOf('permission-denied') !== -1 ? 'permission-denied' : 'unknown')
        this.showSnack('error', this.$t('components.DeletedModels.error.' + reason))
        this.restoreItem = null
      }).then(() => {
        this.restoringId = null
      })
    }
  },
  created () {
    this.loadDeletedModels()
  },
  beforeDestroy () {
    this.stopDeletedModels()
  }
}
</script>
